import React, { useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

import { useReviewsInfiniteScroll } from "../hooks/useReviewsInfiniteScroll"
import { getSectionColorScheme } from "../utils/colorModeUtils"
import { getContrastSafeButtonTextColor } from "../utils/colorUtils"
import { formatFontFamily, formatHeadingFontFamily } from "../utils/fontUtils"
import { getReviewsSectionShowHeader, isHomeBuilderPageType, isReviewsPageType } from "../utils/pageUtils"
import { getThemeShapeClasses } from "../utils/themeUtils"
import { buildDefaultPageUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import { ReviewsInfiniteScrollFooter } from "./shared/ReviewsInfiniteScrollFooter"
import { ScrollRevealItem } from "./shared/ScrollReveal"
import {
  CARD_ROW_HOVER_CLASSES,
  REVIEWS_PAGE_BATCH_SIZE,
  REVIEWS_PAGE_SCROLL_REVEAL_CLASSES,
  REVIEWS_PAGE_STAGGER_MS,
  shouldInstantRevealReview
} from "./shared/scrollRevealVariants"
import { VideoReviewsCarousel } from "./shared/VideoReviewsCarousel"

const ReviewsSectionDesktop = ({
  section,
  practice,
  theme,
  highlightedField,
  pageSlug = null,
  defaultPageType = null
}) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}

  // Check if we're currently on the home page (by defaultPageType or slug fallback)
  const isOnHomePage = isHomeBuilderPageType(defaultPageType, pageSlug, practice)
  const isOnReviewsPage = isReviewsPageType(defaultPageType, pageSlug, practice)
  const reviewsPageUrl = buildDefaultPageUrl(practice, "reviews", "review")

  const showSectionHeader = getReviewsSectionShowHeader(settings, defaultPageType, pageSlug, practice)

  const { allReviews, isLoading, sentinelRef } = useReviewsInfiniteScroll(
    practice?.id,
    practice?.reviews || [],
    isOnReviewsPage
  )

  const showRatings = settings.showRatings || true
  const selectedReviewIds = settings.selectedReviewIds || []
  const showDates = settings.showDates || true
  const showOverallRating = settings.showOverallRating || true
  const showCTA = settings.showCTA || true
  const ctaText = settings.ctaText || "View all reviews"
  const layout = settings.layout || "cards"

  // Get color scheme based on color mode
  const colorScheme = getSectionColorScheme(settings, theme)

  // Use color scheme for colors
  const backgroundColor = colorScheme.sectionBackground
  const defaultTextColor = colorScheme.bodyColor

  // Helper function to get the correct text color
  // In dark mode, always use the color-scheme-aware defaultTextColor (white)
  const isDark = colorScheme.colorMode === "dark"
  const getTextColor = (defaultColor) => (isDark ? defaultTextColor : defaultColor || defaultTextColor)

  // Video review settings
  const showVideoReviews = settings.showVideoReviews !== false // Default to true
  const videoReviewsCount = settings.videoReviewsCount || 3
  const selectedVideoReviewIds = settings.selectedVideoReviewIds || []

  // Text review settings
  const showTextReviews = settings.showTextReviews !== false // Default to true
  const displayCountSetting = settings.displayCount
  const reviewDisplayLimitRaw =
    displayCountSetting === -1
      ? Number.POSITIVE_INFINITY
      : typeof displayCountSetting === "number" && displayCountSetting > 0
      ? displayCountSetting
      : settings.textReviewsCount || 3
  const shouldLimitReviews = !isOnReviewsPage && reviewDisplayLimitRaw !== Number.POSITIVE_INFINITY

  const limitReviews = (reviews) => (shouldLimitReviews ? reviews.slice(0, reviewDisplayLimitRaw) : reviews)

  // Get text reviews (filter out those with video reviews)
  const allTextReviews = (allReviews || []).filter((review) => !review.hasCompletedVideoReview)

  const selectedReviewIdSet = new Set(selectedReviewIds.map((id) => Number(id)))
  const selectedVideoReviewIdSet = new Set(selectedVideoReviewIds.map((id) => Number(id)))

  const baseTextReviews =
    selectedReviewIdSet.size > 0
      ? allTextReviews.filter((review) => selectedReviewIdSet.has(Number(review.id)))
      : allTextReviews

  const selectedTextReviews = limitReviews(baseTextReviews)

  // Get video reviews
  const allVideoReviews = practice?.videoReviews || []
  const baseVideoReviews =
    selectedReviewIdSet.size > 0
      ? allVideoReviews.filter((review) => selectedVideoReviewIdSet.has(Number(review.id)))
      : allVideoReviews.slice(0, videoReviewsCount)

  const selectedVideoReviews = isOnReviewsPage ? allVideoReviews : limitReviews(baseVideoReviews)

  // Combined reviews for overall rating (use all reviews including those with video reviews)
  // allReviews is now managed by local state

  // Get total review count - prioritize practice.reviewsCount over array length
  // The backend passes reviewsCount which is the total count, while reviews array is paginated (only 10)
  const totalReviewsCount = (() => {
    // Try reviewsCount first (camelCase from backend)
    if (practice?.reviewsCount !== undefined && practice.reviewsCount !== null) {
      const count = Number(practice.reviewsCount)
      if (!Number.isNaN(count)) {
        return count
      }
    }
    // Try reviews_count (snake_case fallback)
    if (practice?.reviews_count !== undefined && practice.reviews_count !== null) {
      const count = Number(practice.reviews_count)
      if (!Number.isNaN(count)) {
        return count
      }
    }
    // Last resort: use array length (but this will be limited to 10 due to pagination)
    return allReviews.length
  })()

  // Get average rating from practice object (prioritize direct average over calculation)
  const averageRating =
    practice?.reviewsAverage !== undefined
      ? Number.parseFloat(practice.reviewsAverage).toFixed(1)
      : practice?.reviews_average !== undefined
      ? Number.parseFloat(practice.reviews_average).toFixed(1)
      : allReviews.length > 0
      ? (
          allReviews.reduce((sum, review) => {
            // Try different possible rating field names
            const rating = review.rating || review.score || review.stars || review.ratingValue || 0
            return sum + (rating || 0)
          }, 0) / allReviews.length
        ).toFixed(1)
      : "0.0"

  const VideoReviewCard = ({ videoReview, index }) => {
    const [isMuted, setIsMuted] = useState(true)
    const videoRef = useRef(null)

    // Safety checks for data
    if (!videoReview || !videoReview.id) return null

    // Get client name and anonymization; treat "undefined" (old data) as no name → Anonymous
    const rawFirst = videoReview.client?.firstName || videoReview.user?.firstName || videoReview.unverifiedName || ""
    const rawLast = videoReview.client?.lastName || videoReview.user?.lastName || ""
    const clientName =
      rawFirst != null && String(rawFirst).trim() !== "" && String(rawFirst).trim().toLowerCase() !== "undefined"
        ? String(rawFirst).trim()
        : ""
    const clientLastName =
      rawLast != null && String(rawLast).trim().toLowerCase() !== "undefined" ? String(rawLast).trim() : ""
    const displayName = clientName
      ? `${clientName} ${clientLastName ? `${clientLastName[0]}.` : ""}`.trim() || "Anonymous"
      : "Anonymous"

    // Get rating from review if it exists
    const rating = videoReview.review?.rating || 5

    // Get date - use review date if available, otherwise video review date
    const reviewDate = videoReview.review?.createdAt || videoReview.createdAt
    const formattedDate = reviewDate
      ? new Date(reviewDate)
          .toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric"
          })
          .toUpperCase()
      : ""

    // Get booking count - try multiple possible fields
    const bookingCount =
      videoReview.timesBooked ||
      videoReview.times_booked ||
      videoReview.client?.appointments?.filter(
        (appointment) => appointment.state === "approved" || appointment.state === 2
      )?.length ||
      0

    // Handle hover events for autoplay
    const handleMouseEnter = () => {
      if (videoRef.current) {
        videoRef.current.muted = true // Always ensure muted
        setIsMuted(true) // Update state to match
        videoRef.current.play()
      }
    }

    const handleMouseLeave = () => {
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.currentTime = 0 // Reset to beginning
      }
    }

    // Handle mute toggle
    const handleMuteToggle = (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (videoRef.current) {
        videoRef.current.muted = !videoRef.current.muted
        setIsMuted(videoRef.current.muted)
      }
    }

    return (
      <div
        key={index}
        className={twMerge("relative cursor-pointer overflow-hidden", getThemeShapeClasses(theme))}
        style={{ width: "12rem", height: "20rem" }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        {/* Booking Count Badge */}
        {bookingCount > 1 && (
          <div
            className="absolute left-2.5 top-2.5 z-10 bg-black px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow"
            style={{ borderRadius: "4px" }}>
            BOOKED {bookingCount} TIMES
          </div>
        )}

        {/* Video Player */}
        <video
          ref={videoRef}
          className="size-full object-cover"
          src={videoReview.videoUrl || videoReview.video_url}
          poster={videoReview.thumbnailUrl}
          muted
          loop
          playsInline
          onLoadedMetadata={(e) => {
            // Set a default poster frame if no thumbnail
            if (!videoReview.thumbnailUrl) {
              e.target.currentTime = 1
            }
          }}
        />

        {/* Overlay Content */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        {/* Bottom Content */}
        <div className="absolute inset-x-0 bottom-0 p-4 text-white">
          {/* Client Name */}
          <h3
            className="mb-2 text-sm font-normal"
            style={{
              fontFamily: formatFontFamily(theme?.fonts?.paragraph),
              color: "#FFFFFF"
            }}>
            {displayName}
          </h3>

          {/* Rating Stars */}
          {showRatings && (
            <div className="mb-2 flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <img
                  key={i}
                  src={i < rating ? "/images/email_mailer/star--5.png" : "/images/email_mailer/star--1.png"}
                  alt={i < rating ? "filled star" : "empty star"}
                  className="size-4"
                />
              ))}
            </div>
          )}

          {/* Date */}
          {showDates && formattedDate && (
            <p
              className="text-xs font-semibold uppercase tracking-wide"
              style={{
                fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                color: "#FFFFFF"
              }}>
              {formattedDate}
            </p>
          )}
        </div>

        {/* Mute/Unmute Button */}
        <button
          className={twMerge(
            "absolute bottom-4 right-4 flex size-5 items-center justify-center bg-black/50 text-white transition-colors hover:bg-black/70",
            getThemeShapeClasses(theme)
          )}
          onClick={handleMuteToggle}>
          <span className="text-xs font-bold">{isMuted ? "🔇" : "🔊"}</span>
        </button>
      </div>
    )
  }

  const TextReviewCard = ({ review, index, isExpanded = false }) => {
    const [expanded, setExpanded] = useState(isExpanded)

    // Get card styling based on layout
    const getCardClasses = () => {
      const baseClasses = twMerge(getThemeShapeClasses(theme))
      switch (layout) {
        case "cards":
          return twMerge("flex flex-col gap-6 border border-gray-200 bg-white px-8 py-6", baseClasses)
        case "reviews":
          return twMerge("flex flex-col gap-4 border border-gray-200 bg-white px-6 py-4", baseClasses)
        case "list":
          return twMerge("flex flex-row gap-4 border-b border-gray-200 bg-white px-4 py-3", baseClasses)
        default:
          return twMerge("flex flex-col gap-6 border border-gray-200 bg-white px-8 py-6", baseClasses)
      }
    }

    const reviewDate = review.createdAt
      ? new Date(review.createdAt)
          .toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric"
          })
          .toUpperCase()
      : ""

    // Get client name with anonymization - support unverifiedName for legacy reviews
    // Handle both camelCase and snake_case from API
    const isAnonymous = review.isAnonymous || review.is_anonymous || false
    const clientFirstName = review.client?.firstName || review.client?.first_name
    const clientLastName = review.client?.lastName || review.client?.last_name
    const userFirstName = review.user?.firstName || review.user?.first_name
    const userLastName = review.user?.lastName || review.user?.last_name
    const rawName = clientFirstName || userFirstName || review.unverifiedName || review.unverified_name || ""
    const rawLast = clientLastName || userLastName || ""
    const name =
      rawName != null && String(rawName).trim() !== "" && String(rawName).trim().toLowerCase() !== "undefined"
        ? String(rawName).trim()
        : ""
    const lastName =
      rawLast != null && String(rawLast).trim().toLowerCase() !== "undefined" ? String(rawLast).trim() : ""
    const firstName = (name && name.split(" ")[0]) || ""
    const lastInitial =
      (lastName && `${lastName[0]}.`.toUpperCase()) || (name && name.split(" ")[1]?.toUpperCase()) || ""
    const rawDisplay = [firstName, lastInitial].filter(Boolean).join(" ").trim()
    const displayName = isAnonymous ? "Anonymous" : rawDisplay || "Anonymous"

    // Get booking count - try multiple possible fields
    const bookingCount =
      review.times_booked ||
      review.timesBooked ||
      review.client?.appointments?.filter((appointment) => appointment.state === "approved" || appointment.state === 2)
        ?.length ||
      0

    // Review text handling - truncate after 4 lines
    const reviewText = review.review?.trim() || review.content?.trim() || ""

    // Function to truncate text to approximately 4 lines
    const truncateToLines = (text, maxLines = 4) => {
      if (!text) return ""

      // Split text into words
      const words = text.split(" ")
      const wordsPerLine = 12 // Approximate words per line
      const maxWords = maxLines * wordsPerLine

      if (words.length <= maxWords) return text

      // Truncate to max words and add ellipsis
      return words.slice(0, maxWords).join(" ") + "..."
    }

    const shouldTruncate = reviewText.length > 200 // Keep original logic as fallback
    const displayText = expanded || !shouldTruncate ? reviewText : truncateToLines(reviewText)

    const instantReveal = shouldInstantRevealReview(index, isOnReviewsPage, selectedTextReviews.length)

    return (
      <ScrollRevealItem
        key={index}
        variant={instantReveal ? "none" : "fadeUp"}
        {...(instantReveal
          ? {}
          : isOnReviewsPage
          ? {
              staggerIndex: index % REVIEWS_PAGE_BATCH_SIZE,
              staggerDelayMs: REVIEWS_PAGE_STAGGER_MS,
              threshold: 0.08,
              rootMargin: "0px 0px 4% 0px"
            }
          : { staggerIndex: index + 1 })}
        className={twMerge(
          getCardClasses(),
          CARD_ROW_HOVER_CLASSES,
          isOnReviewsPage && !instantReveal && REVIEWS_PAGE_SCROLL_REVEAL_CLASSES
        )}
        style={{
          borderColor: theme?.colors?.border || "#E6E6E6",
          minWidth: layout === "list" ? undefined : "720px"
        }}>
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            {/* Name and Stars */}
            <div className="flex flex-col gap-2">
              <h3
                className="text-lg font-semibold"
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  color: getTextColor(theme?.colors?.textDark)
                }}>
                {displayName}
              </h3>

              {showRatings && (
                <div className="flex items-center gap-[2px]">
                  {[...Array(5)].map((_, i) => (
                    <img
                      key={i}
                      src={
                        i < (review.rating || 0)
                          ? "/images/email_mailer/star--5.png"
                          : "/images/email_mailer/star--1.png"
                      }
                      alt={i < (review.rating || 0) ? "filled star" : "empty star"}
                      className="size-5"
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Date */}
            {showDates && reviewDate && (
              <span
                className="text-xs font-semibold uppercase tracking-wider"
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  color: getTextColor(theme?.colors?.textDark)
                }}>
                {reviewDate}
              </span>
            )}
          </div>

          {/* Review Text */}
          <div className="flex flex-col gap-4">
            <p
              className="text-base leading-relaxed"
              style={{
                fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                color: getTextColor(theme?.colors?.textDark)
              }}>
              {displayText}
              {shouldTruncate && (
                <>
                  {!expanded ? (
                    <button
                      onClick={() => setExpanded(true)}
                      className="ml-1 text-sm font-semibold underline"
                      style={{
                        color: theme?.colors?.primary || "#BD654F",
                        fontFamily: formatFontFamily(theme?.fonts?.paragraph)
                      }}>
                      Show more
                    </button>
                  ) : (
                    <button
                      onClick={() => setExpanded(false)}
                      className="ml-1 text-sm font-semibold underline"
                      style={{
                        color: theme?.colors?.primary || "#BD654F",
                        fontFamily: formatFontFamily(theme?.fonts?.paragraph)
                      }}>
                      Show less
                    </button>
                  )}
                </>
              )}
            </p>

            {/* Booking Count Badge */}
            {bookingCount > 1 && (
              <div
                className={twMerge(
                  "inline-flex w-fit items-center justify-center px-3 py-1 text-xs font-semibold uppercase tracking-wider",
                  getThemeShapeClasses(theme)
                )}
                style={{
                  backgroundColor: theme?.colors?.backgroundLight || "#F4ECE9",
                  color: getTextColor(theme?.colors?.textDark),
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                }}>
                BOOKED {bookingCount} TIMES
              </div>
            )}
          </div>
        </div>
      </ScrollRevealItem>
    )
  }

  return (
    <section
      className={twMerge(
        "relative flex min-h-[400px] items-center justify-center py-16",
        isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
      )}
      style={{ backgroundColor }}>
      <div className="mx-auto max-w-6xl px-8">
        <div className="flex flex-col items-center gap-6 pb-16">
          {/* Header Section - Hidden on reviews page by default (sub-hero covers it); toggle in editor */}
          {showSectionHeader && (
            <ScrollRevealItem staggerIndex={0} className="flex flex-col items-center gap-6">
              {/* Verified Badge */}
              <div className="flex items-center gap-4">
                <div className="relative size-8">
                  <img src="/images/HealmeLogo.png" alt="Heal.me" className="size-full object-contain" />
                  {/* Checkmark positioned at bottom right */}
                  <div
                    className={twMerge(
                      "absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full shadow-sm",
                      getThemeShapeClasses(theme)
                    )}
                    style={{ backgroundColor: "#0D9DA4" }}>
                    <img src="/images/icons/verified-checkmark.svg" alt="verified" className="size-3" />
                  </div>
                </div>
                <div className={twMerge("flex items-center gap-1 px-0 py-1", getThemeShapeClasses(theme))}>
                  <a
                    href={window.location.origin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold uppercase tracking-wider hover:underline"
                    style={{
                      fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                      color: getTextColor(theme?.colors?.textDark)
                    }}>
                    Heal.me verified Reviews
                  </a>
                </div>
              </div>

              {/* Title */}
              <h2
                className="text-[40px] font-bold leading-tight"
                style={{
                  fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                  fontWeight: theme?.fonts?.headingWeight || "700",
                  color: getTextColor(theme?.colors?.textDark)
                }}>
                What clients are saying
              </h2>

              {/* Overall Rating */}
              {showOverallRating && (selectedTextReviews.length > 0 || selectedVideoReviews.length > 0) && (
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <img
                        key={i}
                        src={
                          i < parseFloat(averageRating)
                            ? "/images/email_mailer/star--5.png"
                            : "/images/email_mailer/star--1.png"
                        }
                        alt={i < parseFloat(averageRating) ? "filled star" : "empty star"}
                        className="size-7"
                      />
                    ))}
                  </div>
                  <span
                    className="text-xs font-semibold uppercase tracking-wider"
                    style={{
                      fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                      color: getTextColor(theme?.colors?.textDark)
                    }}>
                    {totalReviewsCount} Review{totalReviewsCount !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
            </ScrollRevealItem>
          )}

          {/* Top CTA Button - Part of section header; hidden when header is off */}
          {showSectionHeader && showCTA && allReviews.length > 0 && (
            <div className="flex justify-center pb-10">
              <button
                onClick={() => (window.location.href = reviewsPageUrl)}
                className={twMerge(
                  "inline-flex items-center justify-center px-10 py-3 text-base font-normal transition-all",
                  getThemeShapeClasses(theme)
                )}
                style={{
                  backgroundColor: theme?.colors?.primary || "#BD654F",
                  color: getContrastSafeButtonTextColor(theme?.colors?.primary || "#BD654F", theme),
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                }}>
                {ctaText}
              </button>
            </div>
          )}

          {/* Content Section */}
          <div className="flex w-full min-w-0 flex-col gap-8 self-stretch">
            {/* Video Reviews Section */}
            {showVideoReviews && selectedVideoReviews.length > 0 && (
              <VideoReviewsCarousel label="Video reviews">
                {selectedVideoReviews.map((videoReview, index) => (
                  <VideoReviewCard key={videoReview.id} videoReview={videoReview} index={index} />
                ))}
              </VideoReviewsCarousel>
            )}

            {/* Text Reviews Section */}
            {showTextReviews && selectedTextReviews.length > 0 && (
              <div className="flex flex-col items-center gap-8">
                <div
                  className={`w-full max-w-[720px] ${
                    layout === "cards"
                      ? "flex flex-col gap-4"
                      : layout === "reviews"
                      ? "grid grid-cols-1 gap-6 md:grid-cols-2"
                      : layout === "list"
                      ? "flex flex-col gap-2"
                      : "flex flex-col gap-4"
                  }`}
                  style={{ overflowAnchor: "auto" }}>
                  {selectedTextReviews.map((review, index) => (
                    <TextReviewCard key={review.id} review={review} index={index} />
                  ))}
                </div>
              </div>
            )}

            {/* Infinite Scroll Sentinel and Loading Indicator - Only show on reviews page */}
            {isOnReviewsPage && (
              <ReviewsInfiniteScrollFooter
                sentinelRef={sentinelRef}
                isLoading={isLoading}
                textColor={theme?.colors?.textDark}
                paragraphFont={theme?.fonts?.paragraph}
              />
            )}

            {/* View All Reviews Button - Only show on home page and not on reviews page */}
            {isOnHomePage &&
              !isOnReviewsPage &&
              (selectedTextReviews.length > 0 || selectedVideoReviews.length > 0) && (
                <div className="flex justify-center pt-4">
                  <button
                    onClick={() => (window.location.href = reviewsPageUrl)}
                    className={twMerge(
                      "inline-flex items-center justify-center border-2 px-8 py-3 text-base font-normal transition-all",
                      getThemeShapeClasses(theme)
                    )}
                    style={{
                      backgroundColor: "transparent",
                      borderColor: theme?.colors?.primary || "#BD654F",
                      color: theme?.colors?.primary || "#BD654F",
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                      e.target.style.color = "#F4ECE9"
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent"
                      e.target.style.color = theme?.colors?.primary || "#BD654F"
                    }}>
                    {ctaText}
                  </button>
                </div>
              )}

            {/* Empty State */}
            {(!showVideoReviews || selectedVideoReviews.length === 0) &&
              (!showTextReviews || selectedTextReviews.length === 0) && (
                <div className="flex flex-col items-center gap-4 py-12 text-center">
                  <div className="text-6xl">⭐</div>
                  <h3
                    className="text-2xl font-semibold"
                    style={{
                      fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                      color: getTextColor(theme?.colors?.textDark)
                    }}>
                    No reviews yet
                  </h3>
                  <p
                    className="text-lg text-gray-600"
                    style={{
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                    }}>
                    Reviews will appear here once clients leave them
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ReviewsSectionDesktop
